import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useCurrentUserName } from '~/hooks/domain/useCurrentUserName';
import { Patient } from '../domain/patient/patient';
import type { ConnectorPatientManifest } from '../domain/persistence/ConnectorPatientManifest';
import {
    createPatientSession,
    PatientSession,
} from '../domain/session/PatientSession';
import { setActiveSession } from '../domain/session/activeSession';
import { useAppServicesOrNull } from './AppServicesContext';

const SessionContext = createContext<PatientSession | null>(null);

interface Props {
    patient: Patient | null;
    /**
     * Connector identity of the loaded patient, or null for sessions with
     * no connector backing. Published before the patient is selected (see
     * connectorManifestAtoms) and cleared on unload, so in practice it
     * never changes while a patient is loaded.
     */
    connectorManifest: ConnectorPatientManifest | null;
    children: ReactNode;
}

export function PatientSessionProvider({
    patient,
    connectorManifest,
    children,
}: Props) {
    // Null until the engine initializes — this provider mounts from app
    // start (tree shape must not depend on async engine state), so the
    // session is created only once both patient and services exist.
    const appServices = useAppServicesOrNull();
    const [session, setSession] = useState<PatientSession | null>(null);

    // Read through a ref so a change of display name reaches the next save
    // without recreating the live session.
    const currentUserName = useCurrentUserName();
    const currentUserNameRef = useRef(currentUserName);
    useEffect(() => {
        currentUserNameRef.current = currentUserName;
    }, [currentUserName]);
    const readCurrentUserName = useCallback(
        () => currentUserNameRef.current,
        []
    );

    useEffect(() => {
        if (!patient || !appServices) {
            setSession(null);
            setActiveSession(null);
            return;
        }
        const s = createPatientSession(
            appServices,
            patient,
            connectorManifest,
            readCurrentUserName
        );
        setSession(s);
        setActiveSession(s);
        return () => {
            setActiveSession(null);
            s.dispose();
        };
        // The manifest is in the deps for correctness, but its atom's
        // publish/clear ordering (set before patient selection, cleared on
        // unload after deselection) means it does not flip while a patient
        // is loaded, so it never recreates a live session.
    }, [patient, appServices, connectorManifest, readCurrentUserName]);

    return (
        <SessionContext.Provider value={session}>
            {children}
        </SessionContext.Provider>
    );
}

export function useSession(): PatientSession {
    const ctx = useContext(SessionContext);
    if (!ctx) {
        throw new Error(
            'useSession must be used inside PatientSessionProvider with a loaded patient'
        );
    }
    return ctx;
}

export function useSessionOrNull(): PatientSession | null {
    return useContext(SessionContext);
}
